import { MouseEventHandler, RefObject, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import { copyToClipboard } from "utility/misc";

import ServiceConnectionStep from "components/ServiceConnectionStep";

// -----------------------------------------------------------------------------

export default function AzureSPConnectRunCommandPhase(
  props: DisabledProps | ActiveProps
): JSX.Element | null {
  const copyRef: RefObject<HTMLDivElement> = useRef() as any;
  const [userConfirmation, setUserConfirmation] = useState<boolean>(false);

  //
  if (!props.shown) {
    return null;
  }

  //
  const { command, loading, onNext } = props;

  //
  function onContinue() {
    setUserConfirmation(false);
    onNext();
  }

  function toggleUserConfirmation() {
    setUserConfirmation((value) => !value);
  }

  //
  return (
    <ServiceConnectionStep disabled={false}>
      <Box>
        <Typography variant="body1">
          Run the following command in an Azure Powershell terminal to enable
          ThreatKey access:
        </Typography>
        <Paper
          sx={{
            mt: 1,
            fontFamily: "monospace",
            background: "#666",
            color: "#FFF",
            padding: "12px",
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CircularProgress sx={{ color: "#FFF", mr: 1 }} size="16px" />{" "}
              Loading command...
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  userSelect: "all",
                  wordBreak: "break-all",
                }}
                ref={copyRef}
              >
                {command}
              </Box>
              <Button
                variant="contained"
                sx={{ mt: 1 }}
                onClick={() => {
                  (async () => {
                    if (copyRef.current === null) return;
                    copyToClipboard(copyRef.current);
                  })();
                }}
              >
                Copy to clipboard
              </Button>
            </>
          )}
        </Paper>
      </Box>
      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              disabled={loading}
              checked={userConfirmation}
              onChange={toggleUserConfirmation}
            />
          }
          label="I have executed the above instructions and can proceed"
        />
      </Box>
      <Button
        variant="contained"
        disabled={loading || !userConfirmation}
        onClick={onContinue as MouseEventHandler<HTMLButtonElement>}
      >
        Test Connection
      </Button>
    </ServiceConnectionStep>
  );
}

// -----------------------------------------------------------------------------

interface DisabledProps {
  shown: false;
}

interface ActiveProps {
  shown: true;
  loading: boolean;
  command: string;
  onNext: Function;
}
